import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

export const useImageUploadState = (
  onImageUpload?: (file: File) => Promise<string>
) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const uploadImage = useCallback(
    async (file: File): Promise<string> => {
      if (!onImageUpload) {
        throw new Error('No upload handler provided');
      }

      setIsUploading(true);
      setError(null);

      try {
        const imageUrl = await onImageUpload(file);
        toast.success(t('imageUpload.success', 'Image uploaded'));

        return imageUrl;
      } catch (error) {
        const errorInstance =
          error instanceof Error
            ? error
            : new Error(
                t(
                  'imageUpload.errors.uploadFailed',
                  'Failed to upload image. Please try again.'
                )
              );

        setError(errorInstance);

        const message =
          error instanceof Error
            ? error.message
            : t(
                'imageUpload.errors.uploadFailed',
                'Failed to upload image. Please try again.'
              );
        toast.error(message);
        throw errorInstance;
      } finally {
        setIsUploading(false);
      }
    },
    [onImageUpload, t]
  );

  return { uploadImage, isUploading, error };
};

export default useImageUploadState;
